import React from 'react';

import { Line2D, LineSegment2D, Point2D, Vector2D } from '@paragon/geometry-library';
import { TrussEnvelopeShapeWithEdgeSegments } from '@paragon/layout';

import { convertFromLayoutToDesignCoordinates } from '../../../library/layout-geometry/coordinates';
import { layoutColors } from '../layoutColors';
import { Segment } from '../Segments';

type EnvelopeSegments = Pick<TrussEnvelopeShapeWithEdgeSegments, 'backSegment' | 'middleSegment' | 'frontSegment'>;

export function TrussEnvelopeLeftSideIndicator(props: {
  definingPoint: Point2D;
  segments: EnvelopeSegments;
  elevation: number;
}) {
  const { backSegment, frontSegment } = props.segments;

  const baseSegment = new LineSegment2D(
    props.definingPoint.projectOntoLine(frontSegment.toLine()),
    props.definingPoint.projectOntoLine(backSegment.toLine()),
  );
  const envelopeLength = Math.min(backSegment.length, frontSegment.length);

  const triangleOffset = 6.75;
  const triangleHeight = 3;
  const triangleExtensionBeyondEnvelope = 3;

  if (envelopeLength < triangleOffset + triangleHeight) return null;

  const midline = new Line2D(baseSegment.midpoint, backSegment.direction);
  const offsetFromEnd = Vector2D.fromDirectionAndMagnitude(midline.direction, triangleOffset);

  const basePoint = midline.basePoint
    .add(
      Vector2D.fromDirectionAndMagnitude(
        baseSegment.direction.reverse(),
        baseSegment.length / 2 + triangleExtensionBeyondEnvelope,
      ),
    )
    .add(offsetFromEnd);

  const endPoint = midline.basePoint
    .add(
      Vector2D.fromDirectionAndMagnitude(
        baseSegment.direction,
        baseSegment.length / 2 + triangleExtensionBeyondEnvelope,
      ),
    )
    .add(offsetFromEnd);

  const tip = midline.getPointAlongLine(triangleHeight).add(offsetFromEnd);

  function prep(point: Point2D) {
    return convertFromLayoutToDesignCoordinates(point.to3D(props.elevation)).toArray();
  }

  return (
    <>
      <Segment start={prep(basePoint)} end={prep(endPoint)} color={layoutColors.envelopeDirectionIndicator} />
      <Segment start={prep(basePoint)} end={prep(tip)} color={layoutColors.envelopeDirectionIndicator} />
      <Segment start={prep(endPoint)} end={prep(tip)} color={layoutColors.envelopeDirectionIndicator} />
    </>
  );
}
